import { useState } from "react";
import { Check, Lock } from "lucide-react";
import { cn } from "@/lib/utils";
import { PageTitle } from "components/layout";
import { ReactNode } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

export interface Step {
  number: number;
  title: string;
}

interface StepIndicatorProps {
  currentStep: number;
  totalSteps: number;
  steps?: Step[];
  children?: ReactNode;
  onStepClick?: (step: number) => void;
  disabledSteps?: number[];
  disabledStepMessage?: string; // Mensagem customizada para steps bloqueados
  requireConfirmationFrom?: number; // Pede confirmação ao voltar de um step >= este valor
}

const defaultSteps: Step[] = [
  { number: 1, title: "Tipo" },
  { number: 2, title: "Lojas" },
  { number: 3, title: "Config" },
  { number: 4, title: "Produtos" },
  { number: 5, title: "Revisão" },
];

export default function StepIndicator({
  currentStep,
  totalSteps,
  steps = defaultSteps,
  children,
  onStepClick,
  disabledSteps = [],
  disabledStepMessage = "Esta etapa não está disponível para o tipo de campanha selecionado",
  requireConfirmationFrom = 2,
}: StepIndicatorProps) {
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingStep, setPendingStep] = useState<number | null>(null);

  const handleStepClick = (stepNumber: number) => {
    if (!onStepClick) return;

    // Não permitir clicar em steps desabilitados
    if (disabledSteps.includes(stepNumber)) return;

    // Não permitir avançar clicando (só pelo botão de próximo)
    if (stepNumber > currentStep) return;

    // Não fazer nada se clicar no step atual
    if (stepNumber === currentStep) return;

    // Se está voltando e o step atual é >= requireConfirmationFrom, pedir confirmação
    if (stepNumber < currentStep && currentStep >= requireConfirmationFrom) {
      setPendingStep(stepNumber);
      setShowConfirmDialog(true);
    } else {
      // Pode navegar diretamente
      onStepClick(stepNumber);
    }
  };

  const handleConfirm = () => {
    if (pendingStep !== null && onStepClick) {
      onStepClick(pendingStep);
    }
    setShowConfirmDialog(false);
    setPendingStep(null);
  };

  const handleCancel = () => {
    setShowConfirmDialog(false);
    setPendingStep(null);
  };

  // Filtra os steps para mostrar apenas até o totalSteps
  const visibleSteps = steps.slice(0, totalSteps);

  const renderStepContent = (step: Step, isDisabled: boolean, isClickable: boolean) => {
    const stepContent = (
      <div
        className={cn(
          "flex flex-col items-center relative z-10",
          isClickable && "cursor-pointer hover:opacity-80",
          isDisabled && "cursor-not-allowed"
        )}
        onClick={() => handleStepClick(step.number)}
      >
        <div
          className={cn(
            "w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold transition-all duration-300 border-2 bg-background",
            currentStep > step.number && !isDisabled &&
            "border-primary text-primary",
            currentStep === step.number && !isDisabled &&
            "border-primary bg-primary text-primary-foreground ring-4 ring-primary/20",
            currentStep < step.number && !isDisabled &&
            "border-muted text-muted-foreground",
            isDisabled && "border-destructive/50 text-destructive/50 bg-destructive/10"
          )}
        >
          {isDisabled ? (
            <Lock className="h-3.5 w-3.5" />
          ) : currentStep > step.number ? (
            <Check className="h-4 w-4" />
          ) : (
            step.number
          )}
        </div>
        <p
          className={cn(
            "mt-2 text-xs font-medium transition-colors",
            isDisabled && "text-destructive/50"
          )}
          style={{
            color: isDisabled ? undefined : (currentStep >= step.number ? '#2E0D1E' : '#662142')
          }}
        >
          {step.title}
        </p>
      </div>
    );

    if (isDisabled) {
      return (
        <div key={step.number} className="flex-1">
          <Tooltip>
            <TooltipTrigger asChild>
              {stepContent}
            </TooltipTrigger>
            <TooltipContent side="bottom" className="max-w-[200px] text-center">
              <p className="text-xs">{disabledStepMessage}</p>
            </TooltipContent>
          </Tooltip>
        </div>
      );
    }

    return <div key={step.number} className="flex-1">{stepContent}</div>;
  };

  return (
    <>
      <div className="w-full pb-6 pt-2">
        <div className="mb-6">
          <PageTitle title="Criar Nova Campanha" description={`Passo ${currentStep} de ${totalSteps}`} />
        </div>

        {children}

        <div className="mb-6">
          <div className="flex items-center justify-between text-xs mb-2" style={{ color: '#662142' }}>
            <span>{visibleSteps[currentStep - 1]?.title}</span>
            <span>{Math.round((currentStep / totalSteps) * 100)}%</span>
          </div>
          <div className="h-2 bg-muted rounded-full overflow-hidden">
            <div
              className="h-full bg-primary transition-all duration-500 ease-in-out"
              style={{
                width: `${(currentStep / totalSteps) * 100}%`,
              }}
            />
          </div>
        </div>

        <TooltipProvider delayDuration={200}>
          <div className="flex items-center justify-between relative">
            <div className="absolute top-4 left-0 right-0 h-0.5 bg-muted -z-10">
              <div
                className="h-full bg-primary transition-all duration-500 ease-in-out"
                style={{
                  width: `${((currentStep - 1) / (totalSteps - 1)) * 100}%`,
                }}
              />
            </div>

            {visibleSteps.map((step) => {
              const isDisabled = disabledSteps.includes(step.number);
              const isClickable = !!onStepClick && !isDisabled && step.number < currentStep;

              return renderStepContent(step, isDisabled, isClickable);
            })}
          </div>
        </TooltipProvider>
      </div>

      <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Atenção</AlertDialogTitle>
            <AlertDialogDescription>
              Ao voltar para uma etapa anterior, as informações preenchidas nas etapas seguintes podem ser perdidas. Deseja continuar?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleCancel}>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirm}>Continuar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
